import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Button, Segmented, Space, Card } from 'antd';
import {
  PlusOutlined,
  FileExcelOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  CheckOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import HolidaysListView from '../../../components/hr/holidays/HolidaysListView';
import HolidaysCalendarView from '../../../components/hr/holidays/HolidaysCalendarView';
import MarkDefaultHolidaysModal from '../../../components/hr/holidays/MarkDefaultHolidaysModal';
import { holidaysApi, departmentsApi, designationsApi } from '../../../utils/api';
import PermissionGate from '../../../components/PermissionGate';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useListReturnState } from '../../../hooks/useListReturnState';
import moment from 'moment-jalaali';

const Holidays = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = useResourcePermissions('holidays');
  const [viewMode, setViewMode] = useState('list');
  const [markDefaultModalVisible, setMarkDefaultModalVisible] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listFilters, setListFilters] = useState({
    year: moment().format('jYYYY'),
    month: moment().format('jM').padStart(2, '0'),
  });

  const captureCurrentListState = useCallback(
    () => ({
      viewMode,
      listFilters,
    }),
    [viewMode, listFilters],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.viewMode) setViewMode(restored.viewMode);
    if (restored.listFilters) setListFilters(restored.listFilters);
  });

  const parseArrayField = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const toJalaliDate = (dateStr) => {
    const m = moment(dateStr);
    if (!m.isValid()) return dateStr || '-';
    return m.format('jYYYY/jMM/jDD');
  };

  const getWeekday = (dateStr) => {
    const m = moment(dateStr);
    if (!m.isValid()) return '-';
    const dayNames = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];
    return dayNames[m.day()];
  };

  const employmentLabels = {
    fulltime: 'تمام وقت',
    parttime: 'پاره وقت',
    contract: 'قراردادی',
    intern: 'کارآموزی',
    training: 'آموزشی',
    full_time: 'تمام وقت',
    part_time: 'پاره وقت',
  };

  const loadHolidays = async (filters = {}) => {
    setLoading(true);
    try {
      const [res, deptRes, desRes] = await Promise.all([
        holidaysApi.getAll(filters),
        departmentsApi.getAll(),
        designationsApi.getAll(),
      ]);
      setDeptOptions(deptRes.data || []);
      setDesignationOptions(desRes.data || []);
      const data = res.data || [];
      setHolidays(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHolidays({
      year: moment().format('jYYYY'),
      month: moment().format('jM').padStart(2, '0'),
    });
  }, []);

  const handleViewModeChange = (value) => {
    setViewMode(value);
    if (value === 'calendar') {
      loadHolidays(); // بارگذاری بدون فیلتر
    }
  };

  const handleAddHoliday = () => navigate('/hr/holidays/add');
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await holidaysApi.exportExcel({
        year: listFilters.year,
        month: listFilters.month,
      });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export holidays:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };
  const handleMarkDefaultHolidays = () => setMarkDefaultModalVisible(true);
  const handleMarkDefaultClose = () => setMarkDefaultModalVisible(false);

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <GiftOutlined className="text-amber-600 dark:text-amber-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">مدیریت تعطیلات</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-sm m-0">نمایش و مدیریت تعطیلات در حالت لیست یا تقویم</p> */}
              </div>
            </div>
            <Space size="middle" wrap>
              <PermissionGate permission="add_holiday">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddHoliday}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افزودن تعطیلات
                </Button>
              </PermissionGate>
              <PermissionGate permission="add_holiday">
                <Button icon={<CheckOutlined />} onClick={handleMarkDefaultHolidays} size="middle" style={{ fontFamily: 'CustomFont' }}>
                  علامت‌گذاری تعطیلات پیش‌فرض
                </Button>
              </PermissionGate>
              <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} loading={exporting} size="middle" style={{ fontFamily: 'CustomFont' }}>
                خروجی اکسل
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <div className="mb-4">
              <Segmented
                value={viewMode}
                onChange={handleViewModeChange}
                options={[
                  { label: 'لیست', value: 'list', icon: <UnorderedListOutlined /> },
                  { label: 'تقویم', value: 'calendar', icon: <CalendarOutlined /> },
                ]}
                size="large"
                style={{ fontFamily: 'CustomFont' }}
              />
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              {viewMode === 'list' && (
                <HolidaysListView
                  data={holidays}
                  loading={loading}
                  onDelete={canDelete ? async (id) => {
                    await holidaysApi.delete(id);
                    loadHolidays();
                  } : null}
                  onEdit={canEdit ? (id) => navigateToDetail(`/hr/holidays/${id}/edit`) : null}
                  onFilterChange={(filters) => {
                    setListFilters((prev) => ({ ...prev, ...filters }));
                    loadHolidays(filters);
                  }}
                  onBulkAction={canDelete ? async (actionType, rowIds) => {
                    await holidaysApi.applyQuickAction(actionType, rowIds);
                    loadHolidays();
                  } : null}
                  deptOptions={deptOptions}
                  designationOptions={designationOptions}
                  employmentLabels={employmentLabels}
                  toJalaliDate={toJalaliDate}
                  getWeekday={getWeekday}
                />
              )}
              {viewMode === 'calendar' && (
                <HolidaysCalendarView
                  holidays={holidays}
                  deptOptions={deptOptions}
                  designationOptions={designationOptions}
                  employmentLabels={employmentLabels}
                />
              )}
            </div>
          </div>
        </Card>

        <MarkDefaultHolidaysModal
          visible={markDefaultModalVisible}
          onClose={handleMarkDefaultClose}
          onSuccess={() => loadHolidays({ year: moment().format('jYYYY'), month: moment().format('jM').padStart(2, '0') })}
        />
      </div>
    </MainLayout>
  );
};

export default Holidays;
